/**
 * Client-safe shapes for "Music on profiles". The server is the only thing
 * that talks to a catalog; these are what it hands back.
 */

export type MusicProvider = "apple_music";

/**
 * A profile's Music picks are ONE ordered list. The first
 * `PROFILE_SONG_FEATURED` are on display — the CD cases you swipe through —
 * and the rest sit in the list behind "+N", which is also where songs saved
 * from someone else's post or profile land once the display is full.
 */
export const PROFILE_SONG_FEATURED = 5;
/** Most songs one list can hold. */
export const PROFILE_SONG_MAX = 50;

export interface MusicSong {
  provider: MusicProvider;
  /** Catalog id — the ONLY thing a client ever sends back to save a song. */
  id: string;
  title: string;
  artistName: string;
  albumName: string | null;
  /** `{w}x{h}` template; resolve with `musicArtworkSrc`. */
  artworkUrl: string | null;
  artworkBgColor: string | null;
  artworkTextColor: string | null;
  previewUrl: string | null;
  providerUrl: string | null;
  durationMs: number | null;
  explicit: boolean;
  isrc: string | null;
  genreNames: string[];
  /** Credited artists. Empty on search results; filled by the save-time read. */
  artists: Array<{ id: string; name: string }>;
}

export interface MusicArtist {
  provider: MusicProvider;
  id: string;
  name: string;
  artworkUrl: string | null;
  genreNames: string[];
}

export interface MusicAlbum {
  provider: MusicProvider;
  id: string;
  title: string;
  artistName: string;
  artworkUrl: string | null;
  trackCount: number | null;
  releaseYear: number | null;
}

export interface MusicSearchResult {
  songs: MusicSong[];
  artists: MusicArtist[];
  albums: MusicAlbum[];
  /** Offset for the next page of SONGS, or null when exhausted. */
  nextSongOffset: number | null;
}

/** A song as it appears on a profile (the stored snapshot). */
export interface ProfileMusicSong {
  id: string;
  provider: MusicProvider;
  providerSongId: string;
  title: string;
  artistName: string;
  albumName: string | null;
  artworkUrl: string | null;
  artworkBgColor: string | null;
  artworkTextColor: string | null;
  previewUrl: string | null;
  providerUrl: string | null;
  isrc: string | null;
  genreNames: string[];
  explicit: boolean;
}
